import React, { useMemo } from 'react';

import { formatCents } from '@src/common';
import Loading from '@src/components/ui2/Loading';
import { Sale, SaleCouponTypeEnum } from '@src/core/buyerApiClient';
import { useAppSelector } from '@src/core/hooks';
import { currentSaleSelector } from '@src/features/sale';
import useTranslate from '@src/i18n/useTranslate';
import PackageItemGroup from '../../Package/SalePackageItemGroup';
import SaleItem from './SaleItem';
import { PurchaseResume, ResumeValue, SaleItemList, Total } from './styles';
import { groupSaleItemsByProductPackage } from '@src/common/checkout';

interface DrawerProps {
  showTotal?: boolean;
  showPlaceholderImage?: boolean;
}

export const SaleSummary = (props: DrawerProps) => {
  const { showTotal = false, showPlaceholderImage = false } = props;

  const { i18n } = useTranslate();

  const sale = useAppSelector(currentSaleSelector);

  const items = useMemo(() => {
    if (!sale) return [];

    return sale?.items;
  }, [sale]);

  const getSubTotal = (s: Sale) => (s.coupon ? s.coupon.fullAmountCents : s.partialAmountCents);

  const groupedSaleItems = useMemo(() => groupSaleItemsByProductPackage(items), [items]);

  return (
    <>
      {sale ? (
        <>
          <SaleItemList>
            {groupedSaleItems.packages.map((packageItem, index) => (
              <PackageItemGroup
                key={`${packageItem.productPackage.name}-${index}`}
                productPackage={packageItem.productPackage}
                items={packageItem.items}
                showPlaceholderImage={showPlaceholderImage}
              />
            ))}

            {groupedSaleItems.nonPackageItems?.map((item) => (
              <SaleItem key={item.id} item={item} showPlaceholderImage={showPlaceholderImage} />
            ))}
          </SaleItemList>

          <PurchaseResume>
            <ResumeValue>
              <p>{i18n('direct_link_summary_subtotal')}</p>
              <p>{formatCents(getSubTotal(sale))}</p>
            </ResumeValue>

            {sale?.coupon ? (
              <ResumeValue>
                <p>{i18n('direct_link_summary_coupon')}</p>
                <p>{sale?.coupon.code}</p>
              </ResumeValue>
            ) : null}

            {sale?.coupon ? (
              <ResumeValue>
                <p>{i18n('direct_link_summary_discount')}</p>
                <p>
                  {sale?.coupon?.type === SaleCouponTypeEnum.Percentage ? (
                    <span>{`(-${sale?.coupon.formattedDiscount})`}</span>
                  ) : null}

                  {formatCents(sale?.coupon?.fullAmountCents - sale?.coupon.finalAmountCents)}
                </p>
              </ResumeValue>
            ) : null}

            {showTotal ? (
              <Total>
                <p>{i18n('direct_link_summary_total')}</p> <p>{formatCents(sale!.totalAmountCents)}</p>
              </Total>
            ) : null}
          </PurchaseResume>
        </>
      ) : (
        <Loading />
      )}
    </>
  );
};

export default SaleSummary;
